// De dieren binnen één afstandsaanvraag — puur rekenwerk, zonder scherm en zonder server.
//
// Eén dier is één eigen set gegevens, antwoorden, entingen en documenten. Dat klinkt vanzelfsprekend
// maar is de belangrijkste regel van dit hele scherm: informatie van het ene dier mag nooit bij het
// andere terechtkomen. Alles hieronder werkt daarom op een dier-id en nooit op "het huidige dier"
// uit een gedeelde variabele.

import type { AfstandVraag } from "./afstandApi";

/** Eén dier zoals de eigenaar het invult. */
export interface Dier {
  /** Alleen geldig binnen dit scherm; de server deelt zijn eigen nummers uit bij het opslaan. */
  id: string;
  naam: string;
  diersoortId: number | null;
  diersoort: string;
  ras: string;
  /**
   * Het id van het gekozen ras bij het asiel.
   *
   * <p><b>De naam alleen is niet genoeg.</b> Het asiel bewaart een verwijzing naar zijn eigen
   * rassenlijst; stuur je alleen de tekst, dan komt het dier zonder ras binnen en staat er in het
   * dossier een streepje terwijl de eigenaar het wel invulde. Leeg bij een vrij ingetikt ras -- een
   * kruising of een soort die de lijst niet kent.
   */
  rasId: number | null;
  geslacht: string;
  geboortedatum: string;
  chipPasnummer: string;
  /** Per veldsleutel het antwoord; leeg betekent niet beantwoord. */
  antwoorden: Record<string, string>;
  /** Of de eigenaar de vragenwizard helemaal heeft doorlopen. */
  vragenAfgerond: boolean;
}

/** Een leeg dier, met een id dat niet kan botsen. */
export function nieuwDier(volgnummer: number): Dier {
  return {
    id: `dier-${volgnummer}-${Math.random().toString(36).slice(2, 8)}`,
    naam: "",
    diersoortId: null,
    diersoort: "",
    ras: "",
    rasId: null,
    geslacht: "",
    geboortedatum: "",
    chipPasnummer: "",
    antwoorden: {},
    vragenAfgerond: false,
  };
}

/** De velden die een dier altijd moet hebben voor de aanvraag ingediend kan worden. */
const VERPLICHTE_VELDEN: (keyof Dier)[] = ["naam", "diersoort", "geslacht", "geboortedatum"];

/**
 * Hoe ver dit dier is, als percentage.
 *
 * <p>Basisgegevens en vragen tellen allebei mee, want allebei zijn ze nodig. Een dier met alle
 * velden maar zonder vragen staat op de helft, en dat is eerlijker dan "100% ingevuld" met een lege
 * vragenlijst eronder.
 *
 * <p>Geen vragenlijst voor deze diersoort? Dan telt alleen het eerste deel, en is een volledig
 * ingevuld dier gewoon klaar -- niet eeuwig op de helft omdat het asiel niets heeft ingericht.
 */
export function compleetheid(dier: Dier, vragen: AfstandVraag[]): number {
  const velden = VERPLICHTE_VELDEN.length;
  const veldenGevuld = VERPLICHTE_VELDEN.filter((v) => String(dier[v] ?? "").trim() !== "").length;

  const verplichteVragen = vragen.filter((v) => v.verplicht);
  if (verplichteVragen.length === 0) {
    return Math.round((veldenGevuld / velden) * 100);
  }
  const vragenBeantwoord = verplichteVragen
    .filter((v) => (dier.antwoorden[v.id] ?? "").trim() !== "").length;

  // Half om half: de basisgegevens en de vragen wegen even zwaar.
  const deel = veldenGevuld / velden / 2 + vragenBeantwoord / verplichteVragen.length / 2;
  return Math.round(deel * 100);
}

/**
 * Wat er bij dit dier nog ontbreekt, in woorden.
 *
 * <p>"Nog niet compleet" laat iemand zoeken in vier stappen tegelijk. Welk veld het is, is precies
 * het enige dat hij op dat moment wil weten -- en het staat hier bij de gegevens zelf, niet in een
 * foutmelding die hij moet onthouden.
 */
export function watOntbreekt(dier: Dier, vragen: AfstandVraag[]): string[] {
  const uit: string[] = [];
  const labels: Record<string, string> = {
    naam: "roepnaam",
    diersoort: "diersoort",
    geslacht: "geslacht",
    geboortedatum: "geboortedatum",
  };
  for (const veld of VERPLICHTE_VELDEN) {
    if (String(dier[veld] ?? "").trim() === "") {
      uit.push(labels[veld as string] ?? String(veld));
    }
  }

  const open = vragen
    .filter((v) => v.verplicht && zichtbaar(v, dier.antwoorden))
    .filter((v) => (dier.antwoorden[v.id] ?? "").trim() === "");
  if (open.length > 0) {
    uit.push(`${open.length} afstandsvra${open.length === 1 ? "ag" : "gen"}`);
  } else if (vragen.length > 0 && !dier.vragenAfgerond) {
    // Alles beantwoord maar de wizard nooit uitgelopen: dan is de laatste stap niet bevestigd.
    uit.push("de afstandsvragen afronden");
  }
  return uit;
}

/** Of alle verplichte vragen van dit dier beantwoord zijn. */
export function vragenCompleet(dier: Dier, vragen: AfstandVraag[]): boolean {
  return vragen
    .filter((v) => v.verplicht && zichtbaar(v, dier.antwoorden))
    .every((v) => (dier.antwoorden[v.id] ?? "").trim() !== "");
}

/**
 * De stappen van de wizard: de groepen van de vragenlijst, op volgorde.
 *
 * <p>De groepen van het asiel bepalen de stappen en niet andersom. Een vaste lijst van vier zou
 * betekenen dat de vijfde groep die een asiel aanmaakt stilletjes ergens achteraan wordt geplakt,
 * of erger: verdwijnt. De standaardlijst heeft er vier, dus standaard zie je er vier.
 */
export function stappenVan(vragen: AfstandVraag[]): string[] {
  const uit: string[] = [];
  for (const v of vragen) {
    if (!uit.includes(v.groep)) {
      uit.push(v.groep);
    }
  }
  return uit;
}

/** De vragen van één stap. */
export function vragenVanStap(vragen: AfstandVraag[], groep: string): AfstandVraag[] {
  return vragen.filter((v) => v.groep === groep);
}

/**
 * Of een vraag nu getoond moet worden.
 *
 * <p>Vervolgvragen horen pas te verschijnen als ze ergens over gaan: "waar beet het dier?" is een
 * onbeleefde vraag aan iemand wiens dier nooit gebeten heeft, en een verplicht veld dat niemand kan
 * beantwoorden blokkeert de hele aanvraag.
 *
 * <p>De koppeling staat in de vraagtekst van de backoffice als {@code [toon_als:sleutel=waarde]}.
 * Staat er niets, dan is de vraag altijd zichtbaar.
 */
export function zichtbaar(vraag: AfstandVraag, antwoorden: Record<string, string>): boolean {
  const regel = toonAls(vraag);
  if (!regel) {
    return true;
  }
  return (antwoorden[regel.sleutel] ?? "").trim().toLowerCase() === regel.waarde.toLowerCase();
}

/** De voorwaarde uit de vraagtekst, als die er is. */
export function toonAls(vraag: AfstandVraag): { sleutel: string; waarde: string } | null {
  const match = /\[toon_als:([^=\]]+)=([^\]]+)\]/.exec(vraag.vraag ?? "");
  if (!match) {
    return null;
  }
  return { sleutel: match[1].trim(), waarde: match[2].trim() };
}

/** De vraagtekst zoals de eigenaar hem leest: zonder de voorwaarde erin. */
export function vraagTekst(vraag: AfstandVraag): string {
  return (vraag.vraag ?? "").replace(/\s*\[toon_als:[^\]]+\]\s*/, " ").trim();
}

/** "4 jaar" bij een geboortedatum, of niets als die onbekend of onleesbaar is. */
export function leeftijd(geboortedatum: string): string | null {
  if (!geboortedatum) return null;
  const dag = new Date(geboortedatum);
  if (Number.isNaN(dag.getTime())) return null;
  const jaren = Math.floor((Date.now() - dag.getTime()) / (365.25 * 24 * 3600 * 1000));
  if (jaren < 0) return null;
  return jaren < 1 ? "< 1 jaar" : `${jaren} jaar`;
}

/**
 * Wat er verandert als de eigenaar iets in het rasveld tikt of kiest.
 *
 * <p><b>De diersoort volgt het ras.</b> Wie "Labrador" intikt weet wel dat het een hond is; hem
 * eerst een keuzelijst laten afwerken om dat te bevestigen is werk dat het formulier zelf kan doen
 * (Maarten, 26-10-2026). Een ras hoort bij precies één soort, dus die keuze is niet geraden.
 *
 * <p><b>Alleen een exacte naam telt als gekozen.</b> Halverwege het typen staat er "Labrad", en dat
 * is geen ras — dan gaat alleen de tekst mee en blijft het id leeg. Het asiel legt zo'n vrije tekst
 * bij het inboeken alsnog op een echt ras; een half woord als verwijzing doorgeven zou erger zijn,
 * want dat ziet eruit als een gegeven.
 */
export function rasKeuze(
  tekst: string,
  rassen: { id: number; naam: string; diersoortId: number | null }[],
  diersoorten: { id: number; naam: string }[],
): Partial<Dier> {
  const gekozen = rassen.find((r) => r.naam.toLowerCase() === tekst.trim().toLowerCase());
  if (!gekozen) {
    return { ras: tekst, rasId: null };
  }
  const soort = diersoorten.find((s) => s.id === gekozen.diersoortId);
  return soort
    ? { ras: gekozen.naam, rasId: gekozen.id, diersoortId: soort.id, diersoort: soort.naam }
    : { ras: gekozen.naam, rasId: gekozen.id };
}

/**
 * Wat er verandert als de eigenaar een andere diersoort kiest.
 *
 * <p>Een ras dat niet bij de nieuwe soort hoort gaat eruit. Laat je het staan, dan komt er een
 * hondenras bij een kat binnen -- en dat ziet er bij het asiel uit als iets wat de eigenaar zo
 * bedoeld heeft.
 */
export function diersoortKeuze(
  gekozen: { id: number; naam: string } | undefined,
  dier: Dier,
  rassen: { id: number; naam: string; diersoortId: number | null }[],
): Partial<Dier> {
  const verandering: Partial<Dier> = {
    diersoortId: gekozen?.id ?? null,
    diersoort: gekozen?.naam ?? "",
  };
  const huidigRas = rassen.find((r) => r.id === dier.rasId);
  if (huidigRas && huidigRas.diersoortId !== (gekozen?.id ?? null)) {
    verandering.ras = "";
    verandering.rasId = null;
  }
  return verandering;
}

/**
 * De dieren uit een eerder ingediende aanvraag terug in het formulier.
 *
 * <p><b>Waarom dit moet.</b> Wie terugkomt om aan te vullen, stuurt bij het opnieuw indienen de hele
 * set dieren -- dat is hoe het asiel een momentopname ontvangt. Begon hij met een leeg formulier,
 * dan zou dat zijn dieren wissen. Hij hoort ze terug te zien zoals hij ze opgaf (Maarten, 26-10-2026).
 *
 * <p>De antwoorden komen per veldsleutel terug en gaan weer in dezelfde vorm het formulier in. Het
 * ras komt als naam terug; het id zoekt het formulier er zelf bij op, net als bij intikken.
 */
export function dierenUitOpgave(
  dieren: {
    id: number;
    naam: string | null;
    diersoortId: number | null;
    diersoort: string | null;
    ras: string | null;
    geslacht: string | null;
    geboortedatum: string | null;
    chipPasnummer: string | null;
  }[],
  antwoorden: { dierId: number; antwoorden: { veldSleutel: string; waarde: string | null }[] }[],
): Dier[] {
  return dieren.map((d, i) => {
    const vanDier = antwoorden.find((a) => a.dierId === d.id);
    const gegeven: Record<string, string> = {};
    for (const a of vanDier?.antwoorden ?? []) {
      if (a.waarde) {
        gegeven[a.veldSleutel] = a.waarde;
      }
    }
    return {
      ...nieuwDier(i + 1),
      /*
       * Een vaste sleutel, afgeleid van het dier bij het asiel.
       *
       * `nieuwDier` verzint er een met een toevalsgetal erin. Dat is goed voor een dier dat net
       * wordt aangemaakt, maar niet hier: alles wat aan een dier hangt en niet in dit object zit
       * -- de bijlagen, de foto's, het entingenboekje -- wordt op deze sleutel gevonden. Kreeg het
       * dier bij elke heropening een nieuwe, dan raakte een zojuist toegevoegde foto los en leek
       * hij verdwenen (Maarten, 27-10-2026).
       */
      id: `opgave-${d.id}`,
      naam: d.naam ?? "",
      diersoortId: d.diersoortId,
      diersoort: d.diersoort ?? "",
      ras: d.ras ?? "",
      rasId: null,
      geslacht: d.geslacht ?? "",
      geboortedatum: d.geboortedatum ?? "",
      chipPasnummer: d.chipPasnummer ?? "",
      antwoorden: gegeven,
      // Hij heeft ze eerder afgerond; anders zou het formulier hem de hele wizard opnieuw laten
      // doorlopen voor een aanvulling die ergens anders zit.
      vragenAfgerond: Object.keys(gegeven).length > 0,
    };
  });
}
